/**
 * ★ THE AUTHOR'S PHOTO IN THE SHELL (design 24, T13) - in the sidebar's round mark and the top
 * bar's round button, where the shell drew an initial by design.
 *
 * WHY IT WAS AN INITIAL: "the session carries no picture, and a second read on every screen to
 * fetch one would be a read for decoration" - the shell is mounted around each screen
 * (src/router.tsx, `gated`). The photos were migrated - the import links each author's old picture
 * as the profile photo - and the editor's byline already showed it from `getOwnProfile`
 * (routes/articles/editor/AboutThisPiece.tsx); only the shell drew a letter ("H" for Hanna).
 *
 * READ ONCE PER CABINET LOAD, NOT ONCE PER SCREEN. The first mark that needs the photo asks
 * `getOwnProfile`; the answer is kept here per client and per person - a WeakMap, as the sidebar's
 * counts are kept (`useNavAttention`, @moonx/ui), so a dropped client takes its own and the next
 * author signed in on this tab never sees the last one's face - and every later screen draws it at
 * once with no read. The read is shared, so a screen that leaves does not cancel it.
 *
 * THE INITIALS STAY while the read is out, when the profile has no photo, when the read fails, and
 * when the picture itself will not load: the emulator's address first on a laptop, then the API's,
 * as the byline does (`useServedImageSrc`), then the initials.
 *
 * A NEW PHOTO: `refreshAccountPhoto()` forgets what was read, and every mark on screen reads again,
 * so a save that changes the photo shows it at once. Without that call it shows at the next load.
 *
 * THE SAME READ GIVES THE LANGUAGE the cabinet starts in for an author who never chose one in this
 * browser (`ownProfileOnce`, read by ./CabinetShell.tsx), so that costs no second request.
 */
import { useEffect, useState } from 'react';
import { useAuthSession } from '../auth/session/AuthSessionProvider';
import { useCabinetApi } from '../cabinet/api';
import type { CabinetApi } from '../cabinet/api';
import { useServedImageSrc } from '../storage/emulator-image-url';
import classes from './cabinet-frame.module.css';

type OwnProfile = Awaited<ReturnType<CabinetApi['getOwnProfile']>>;

/** One person's photo: the read, and once it has settled, its answer (`null`: none to show). */
interface PhotoRead {
  /** The profile itself, `null` when the read failed. */
  readonly profile: Promise<OwnProfile | null>;
  readonly read: Promise<string | null>;
  settled: boolean;
  photo: string | null;
}

/** Per client, per person. `refreshAccountPhoto` replaces it: a WeakMap cannot be cleared. */
let remembered = new WeakMap<CabinetApi, Map<string, PhotoRead>>();

/** The marks on screen, told to read again when the photo changed. */
const listeners = new Set<() => void>();

/** A profile's photo address, or `null` for none: an empty string is not a picture. */
function photoOf(avatarUrl: string | null | undefined): string | null {
  return typeof avatarUrl === 'string' && avatarUrl.trim().length > 0 ? avatarUrl : null;
}

function photoRead(api: CabinetApi, identity: string): PhotoRead {
  let byPerson = remembered.get(api);
  if (byPerson === undefined) {
    byPerson = new Map();
    remembered.set(api, byPerson);
  }
  const known = byPerson.get(identity);
  if (known !== undefined) {
    return known;
  }
  // No signal: the read is shared by every screen of this load, and one leaving must not end it.
  const profile = api.getOwnProfile().then(
    (answer): OwnProfile | null => answer,
    () => null,
  );
  const entry: PhotoRead = {
    profile,
    read: profile.then((answer) => photoOf(answer?.avatarUrl)),
    settled: false,
    photo: null,
  };
  void entry.read.then((photo) => {
    entry.settled = true;
    entry.photo = photo;
  });
  byPerson.set(identity, entry);
  return entry;
}

/** The signed-in author's profile, read once per cabinet load with the photo; `null` if it failed. */
export function ownProfileOnce(api: CabinetApi, identity: string): Promise<OwnProfile | null> {
  return photoRead(api, identity).profile;
}

/** What was read already for this person, so a new screen draws the photo with no wait. */
function settledPhoto(api: CabinetApi, identity: string): string | null {
  const known = remembered.get(api)?.get(identity);
  return known?.settled === true ? known.photo : null;
}

/** The signed-in author's photo address, or `null` while there is none to show. */
function useAccountPhoto(api: CabinetApi, identity: string): string | null {
  // Whose photo it is, too: a mark still on screen when another author signs in shows theirs only.
  const [shown, setShown] = useState(() => ({
    api,
    identity,
    photo: settledPhoto(api, identity),
  }));
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const readAgain = (): void => {
      setGeneration((last) => last + 1);
    };
    listeners.add(readAgain);
    return () => {
      listeners.delete(readAgain);
    };
  }, []);

  useEffect(() => {
    let live = true;
    void photoRead(api, identity).read.then((photo) => {
      if (live) {
        setShown({ api, identity, photo });
      }
    });
    return () => {
      live = false;
    };
  }, [api, identity, generation]);

  return shown.api === api && shown.identity === identity
    ? shown.photo
    : settledPhoto(api, identity);
}

/**
 * Forget the photo read for this load, and have every mark on screen read it again - for the save
 * that changes it. Until a save calls it, a new photo shows at the next load.
 */
export function refreshAccountPhoto(): void {
  remembered = new WeakMap();
  for (const readAgain of listeners) {
    readAgain();
  }
}

/**
 * The signed-in author's photo filling a round mark, or `initials` while there is none to show.
 * Decoration (`alt=""`): the name is beside the sidebar's mark, and in the top bar's link's name.
 */
export function AccountPhoto({ initials }: { readonly initials: string }) {
  const { state } = useAuthSession();
  const photo = useAccountPhoto(useCabinetApi(), state.identity?.uid ?? '');
  const served = useServedImageSrc(photo);
  const [broken, setBroken] = useState<string | null>(null);

  if (served.src === null || served.src === broken) {
    return initials;
  }
  const src = served.src;
  return (
    <img
      src={src}
      alt=""
      className={classes.photo}
      onError={() => {
        // The emulator's copy first, then the API's address once (`useServedImageSrc`), then none.
        if (src !== photo) {
          served.onError();
        } else {
          setBroken(src);
        }
      }}
    />
  );
}
